import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import PortalLayout from './shared/components/PortalLayout';
import Login from './shared/components/Login';
import ForgotPassword from './shared/components/ForgotPassword';
import ResetPassword from './shared/components/ResetPassword';
import UserManagement from './shared/components/UserManagement';
import RequireRole from './shared/guards/RequireRole';
import { cashRoutes } from './modules/cash/routes';
import { salaryRoutes } from './modules/salary/routes';
import { profileRoutes } from './modules/profile/routes';
import { checklistsRoutes } from './modules/checklists/routes';
import { myPayRoutes } from './modules/my-pay/routes';
import { trainingRoutes } from './modules/training/routes';
import { inventoryV2Routes } from './modules/inventory-v2/routes';
import { cafeMenuRoutes } from './modules/cafe-menu/routes';
import { recipeRoutes } from './modules/recipes/routes';
import { getFirstAllowedPath } from './modules/registry';
import './App.css';
import './styles/mobile.css';
import Today from './shared/components/Today';
import Help from './modules/help/Help';
import EditProtection from './shared/components/EditProtection';

const RootRedirect: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  return <Navigate to={getFirstAllowedPath(user.role)} replace />;
};

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  // /sign-up and /verify-email removed: accounts are created by an Owner or
  // Admin, who triggers an invite email. Both API routes now return 410.
  {
    path: '/',
    element: <EditProtection><PortalLayout /></EditProtection>,
    children: [
      { index: true, element: <RootRedirect /> },
      { path: 'today', element: <Today /> },
      { path: 'help', element: <Help /> },
      ...cashRoutes,
      ...salaryRoutes,
      ...profileRoutes,
      ...checklistsRoutes,
      ...myPayRoutes,
      ...trainingRoutes,
      ...inventoryV2Routes,
      ...cafeMenuRoutes,
      ...recipeRoutes,
      {
        path: 'users',
        element: (
          <RequireRole allowedRoles={['Owner', 'Admin', 'Manager']}>
            <UserManagement />
          </RequireRole>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <RouterProvider router={router} />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
